import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Space from 'components/generic/Space.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipContentReceptionist from 'components/generic/TooltipContentReceptionist.component';
import CONF from 'config/conf.singleton';
import S from 'constants/string.const';
import { useHelpLinks } from 'hooks/useHelpLinks.hook';
import { useOnlineBookingPermissions } from 'hooks/useOnlineBookingPermissions.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonNoShowProtection, selectCurrentSalonStripeAccount } from 'selectors/currentSalon.selectors';
import fromAnyToString from 'utils/converters/fromAnyToString';
import pageScrollToTop from 'utils/dom/pageScrollToTop.util';
import logger from 'utils/logger.util';

import { fetchSalonState } from 'salon/actions';
import { setSalonNoshowOrDeposit } from 'settings/online-booking/actions';
import NoShowModal from 'settings/page-elements/setup-nsp-deposit/modals/NoShowModal';
import PaymentFeesModal from 'settings/page-elements/setup-nsp-deposit/modals/PaymentFeesModal';
import StripeModal from 'settings/page-elements/setup-nsp-deposit/modals/StripeModal';

import CN from './NoShowProtectionSetup.module.scss';

const L = logger('NoShowProtectionSetup');

const NoShowProtectionSetup = ({ errorEnableModal, onNoShowCheckboxClick }) => {
  const dispatch = useDispatch();
  const salonId = useCurrentSalonId();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { noShowProtectionHelpLink } = useHelpLinks();

  const hasStripeAccount = useSelector(selectCurrentSalonStripeAccount);
  const hasNoShowProtection = useSelector(selectCurrentSalonNoShowProtection);
  const { canManageDepositSettings } = useOnlineBookingPermissions();
  const isReadOnly = !canManageDepositSettings;
  const randomKey = useSelector((state) => state.currentSalon.random_key);
  const email = useSelector((state) => (state.settings.billing.stripeCustomerData ? state.settings.billing.stripeCustomerData.email : ''));
  const isFetchingRandomKey = useSelector((state) => state.currentSalon.isFetchingRandomKey);

  const { val: isStripeRendered, yay: showModalStripe, nay: hideModalStripe } = useTip(false);
  const { val: isFeesRendered, yay: showModalFees, nay: hideModalFees } = useTip(false);
  const { val: isNoShowOpen, yay: snowModalNoShow, nay: hideModalNoshow } = useTip(false);

  const isNoShowRendered = isNoShowOpen && !errorEnableModal;

  const handleNoShow = useCallback(
    (data) => {
      showModalFees();
      hideModalNoshow();
      dispatch(setSalonNoshowOrDeposit(data, salonId));
      pageScrollToTop();
    },
    [dispatch, hideModalNoshow, salonId, showModalFees],
  );

  const handleSetup = useCallback(() => {
    snowModalNoShow();
    pageScrollToTop();
  }, [snowModalNoShow]);

  const handleClose = useCallback(() => {
    hideModalNoshow();
    hideModalFees();
  }, [hideModalFees, hideModalNoshow]);

  const handlePaymentFees = useCallback(() => {
    hideModalFees();
    showModalStripe();

    if (hasStripeAccount) {
      hideModalStripe();
    } else {
      window.location.replace(`${CONF.stripeRedirectUrl}&state=${randomKey}&stripe_user[email]=${email}&suggested_capabilities[]=transfers`);
    }

    pageScrollToTop();
  }, [email, hasStripeAccount, hideModalFees, hideModalStripe, randomKey, showModalStripe]);

  useEffect(() => {
    if (randomKey || isFetchingRandomKey) return;
    L.unignore('useEffect()', 'fetchSalonState:', dispatch(fetchSalonState(fromAnyToString(salonId))));
  }, [dispatch, isFetchingRandomKey, randomKey, salonId]);

  // if (!hasNoShowProtection) return null;

  return (
    <div
      data-bem="NoShowProtectionSetup"
      data-testid="NoShowProtectionSetup"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      {hasStripeAccount ? (
        <div className={CN.content}>
          <div className={CN.title}>
            <div className={CN.flex}>
              {t('onlineBooking.generalSettings.businessStripeAccount.header3')}
              <div className={CN.gap}>
                <Badge variant="success">
                  <span className={CN.emojiSize}>{S.like}</span>
                  <Space>{t('onlineBooking.generalSettings.businessStripeAccount.badge1')}</Space>
                </Badge>
              </div>
            </div>

            {isReadOnly ? (
              <Tooltip variant="top" content={<TooltipContentReceptionist />}>
                <Toggler tag="NoShowProtectionSetup-toggle" data-testid="NoShowProtectionSetup-toggle" selected={hasNoShowProtection} />
              </Tooltip>
            ) : (
              <Toggler tag="NoShowProtectionSetup-toggle" data-testid="NoShowProtectionSetup-toggle" onClick={onNoShowCheckboxClick} selected={hasNoShowProtection} />
            )}
          </div>
          <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description4')}</div>
        </div>
      ) : (
        <div className={CN.content}>
          <div className={CN.title}>{t('onlineBooking.generalSettings.businessStripeAccount.header3')}</div>
          <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description5')}</div>

          <div className={CN.buttonsContainer}>
            {isReadOnly ? (
              <Tooltip variant="top" content={<TooltipContentReceptionist />}>
                <Button variant="primary" data-testid="NoShowProtectionSetup__setup-button">
                  {t('onlineBooking.generalSettings.businessStripeAccount.confirmButton')}
                </Button>
              </Tooltip>
            ) : (
              <Button onClick={handleSetup} variant="primary" data-testid="NoShowProtectionSetup__setup-button">
                {t('onlineBooking.generalSettings.businessStripeAccount.confirmButton')}
              </Button>
            )}

            <ExternalLink link={noShowProtectionHelpLink} variant="primary">
              {t('onlineBooking.generalSettings.businessStripeAccount.linkLabel')}
            </ExternalLink>
          </div>
        </div>
      )}
      {isNoShowRendered && (
        <NoShowModal
          btnName={t('onlineBooking.generalSettings.businessStripeAccount.okButton')}
          popupTitle={t('onlineBooking.generalSettings.businessStripeAccount.noShowModal.title')}
          onClickButton={handleNoShow}
          closeModal={handleClose}
        />
      )}
      {/* NOTE: this is old design ! 
      TODO: replace with BeforeSetupStripeModal - check DepositSetup.jsx */}
      {isFeesRendered && (
        <PaymentFeesModal
          btnName={t('onlineBooking.generalSettings.businessStripeAccount.okButton')}
          popupTitle={t('onlineBooking.generalSettings.businessStripeAccount.noShowModal.title')}
          onClickButton={handlePaymentFees}
          closeModal={handleClose}
        />
      )}
      {isStripeRendered && (
        <StripeModal>
          <div className="spinner-text">{t('onlineBooking.generalSettings.businessStripeAccount.stripeModalText')}</div>
        </StripeModal>
      )}
    </div>
  );
};

export default NoShowProtectionSetup;
